/**
 * Client state + API glue for the custom CMS drawer.
 *
 * Boundary note: `canEdit` here only decides whether the drawer chrome renders.
 * It is NOT a security control — every `/api/cms/*` endpoint re-verifies the BFF
 * session + editor claim server-side (`server/utils/cms-guard.ts`). A user who
 * flips this flag in devtools gets a drawer whose every action 403s.
 *
 * Editing model: the published source is the fork base; edits live in a draft
 * (autosaved via the draft API) until Publish commits to the content repo. The
 * drawer edits frontmatter (YAML text, P1) and body (MDC, via `e-richtext-editor`)
 * separately, mirroring the server's `{ frontmatter, body }` split.
 */
// `yaml` (~40 KB browser build) is only needed to parse/serialize CMS frontmatter
// — an admin-only path. But this composable is instantiated on every page (app.vue),
// so a static import shipped the parser to every visitor in the entry chunk. Load
// it lazily on first editor use instead. `yamlReady` is a reactive latch so the
// `frontmatter` computed re-evaluates once the module resolves.
let yamlParse: ((src: string) => unknown) | null = null;
let yamlStringify: ((obj: unknown, opts?: { lineWidth?: number }) => string) | null = null;
const yamlReady = ref(false);
let yamlLoading: Promise<void> | null = null;
function ensureYaml(): Promise<void> {
  if (yamlReady.value) return Promise.resolve();
  if (!yamlLoading) {
    yamlLoading = import("yaml").then((m) => {
      yamlParse = m.parse;
      yamlStringify = m.stringify;
      yamlReady.value = true;
    });
  }
  return yamlLoading;
}

export interface CmsDiffFile {
  path: string;
  relPath: string;
  published: string;
  proposed: string;
  hasChanges: boolean;
}

export interface CmsRelease {
  ready: boolean;
  served: string | null;
  production: string | null;
  blocked: string | null;
  /** Summary of what DEPLOY ships (server/utils/cms-release.ts ReleasePending). */
  pending: {
    commits: number;
    truncated: boolean;
    authors: Array<{ name: string; commits: number }>;
    latest: Array<{ sha: string; message: string; author: string; date: string }>;
    oldest: string | null;
    files: number;
    filesTruncated: boolean;
  } | null;
  /** A deploy to production is underway (server/utils/cms-release.ts). */
  deploying: { since: string; url: string } | null;
  lastDeployFailed: boolean;
  dev?: boolean;
}

export interface CmsDraftDto {
  path: string;
  relPath: string;
  frontmatter: Record<string, unknown>;
  body: string;
  baseSha: string | null;
  editorEmail: string;
  updatedAt: number;
}

export interface CmsFieldDescriptor {
  key: string;
  type: "string" | "text" | "number" | "boolean" | "enum" | "string[]";
  optional: boolean;
  default?: unknown;
  options?: string[];
}

interface SourceResponse {
  path: string;
  relPath: string;
  fields: CmsFieldDescriptor[];
  published: {
    frontmatter: Record<string, unknown>;
    body: string;
    sha: string | null;
    exists: boolean;
  };
  draft: CmsDraftDto | null;
}

export function useCmsEditor() {
  // Shared singletons across every consumer (shell + panel + pages).
  const canEdit = useState("cms:canEdit", () => false);
  // `enabled` = canEdit AND the environment allows the CMS UI (dev / CMS_ENABLED).
  // The split-view scaffold mounts only when enabled — never for non-editors or
  // on production.
  const enabled = useState("cms:enabled", () => false);
  // `editing` = enabled AND page editing is switched on (CMS_EDITING). Off, the
  // CMS is deploy-only: no editor, no splitter, just the DEPLOY button.
  const editing = useState("cms:editing", () => false);
  const probed = useState("cms:probed", () => false);
  const open = useState("cms:open", () => false); // editor panel expanded?
  const editorName = useState("cms:editorName", () => ""); // for PR attribution display
  const editorEmail = useState("cms:editorEmail", () => ""); // for the PR-title [username] tag

  // Live-preview override: the parsed AST body of the in-progress edit, plus the
  // route it applies to. Pages splice this onto their ContentRenderer value so
  // the real page updates as you type. Cleared when not editing.
  const previewBody = useState<unknown | null>("cms:previewBody", () => null);
  const previewPath = useState<string | null>("cms:previewPath", () => null);

  // True whenever the editing shell is active (an enabled editor, CMS_EDITING on).
  // Provided app-wide so scroll/entry animations go STATIC: the page then lives
  // inside the splitter's scrolling panel where window-scroll-based reveals
  // (IntersectionObserver, GSAP ScrollTrigger) don't fire, and GSAP SplitText
  // DOM mutation would be corrupted by live-preview re-patching. It's not gated
  // on `open` because the page is in the panel even when the editor is collapsed.
  // Deploy-only mode has no splitter, so the page keeps its normal animations.
  const previewing = computed(() => editing.value);

  // Editing buffers for the current page.
  const loading = useState("cms:loading", () => false);
  const saving = useState("cms:saving", () => false);
  const publishing = useState("cms:publishing", () => false);
  const deploying = useState("cms:deploying", () => false);
  const error = useState<string | null>("cms:error", () => null);
  const relPath = useState<string | null>("cms:relPath", () => null);
  const baseSha = useState<string | null>("cms:baseSha", () => null);
  const frontmatterYaml = useState("cms:fmYaml", () => "");
  // Frontmatter field descriptors (from the page's Zod schema) driving the
  // Settings form. Populated by loadCurrent from the source response.
  const schemaFields = useState<CmsFieldDescriptor[]>("cms:schemaFields", () => []);
  const body = useState("cms:body", () => "");
  const hasDraft = useState("cms:hasDraft", () => false);
  const lastSavedAt = useState<number | null>("cms:lastSavedAt", () => null);
  // Snapshot of the values as loaded, to detect unsaved edits (dirty) — e.g.
  // before navigating away, so the previous page's changes can be autosaved.
  const loadedBody = useState("cms:loadedBody", () => "");
  const loadedFm = useState("cms:loadedFm", () => "");

  const route = useRoute();

  /** One-time probe of edit rights; drives whether the CMS scaffold mounts. */
  async function probe(): Promise<void> {
    if (probed.value) return;
    probed.value = true;
    try {
      const res = await $fetch<{
        canEdit: boolean;
        enabled: boolean;
        editing: boolean;
        name?: string;
        email?: string;
      }>("/api/cms/session");
      canEdit.value = res.canEdit;
      enabled.value = res.enabled;
      editing.value = res.editing;
      editorName.value = res.name ?? "";
      editorEmail.value = res.email ?? "";
    } catch {
      canEdit.value = false;
      enabled.value = false;
      editing.value = false;
    }
  }

  /**
   * Throttled server-side re-parse of the current body → live preview override.
   * Runs on the server (`/api/cms/preview`) because the MDC parser can't run in
   * the browser bundle (CJS interop). Sets `previewBody`/`previewPath` which the
   * page's ContentRenderer picks up via `previewValue`.
   */
  async function refreshPreview(): Promise<void> {
    try {
      const doc = await $fetch<{ body?: unknown }>("/api/cms/preview", {
        method: "POST",
        body: { markdown: body.value },
      });
      previewBody.value = doc?.body ?? null;
      previewPath.value = route.path;
    } catch {
      // leave the last good preview in place on a transient parse error
    }
  }

  function clearPreview(): void {
    previewBody.value = null;
    previewPath.value = null;
  }

  /**
   * Splice the live-preview body onto a page object when this route is being
   * previewed. Pages call `:value="cms.previewValue(page)"` — reactive on
   * `previewBody`, so the real page re-renders as the editor types. No-op (returns
   * the page unchanged) when not editing this route.
   */
  function previewValue<T>(page: T): T {
    if (
      enabled.value &&
      previewBody.value &&
      previewPath.value === route.path &&
      page &&
      typeof page === "object"
    ) {
      return { ...(page as object), body: previewBody.value } as T;
    }
    return page;
  }

  /** Load the editable source for the current route (draft takes precedence). */
  async function loadCurrent(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      // Editing starts here → pull in the yaml parser before toYaml/parseFrontmatter run.
      await ensureYaml();
      const res = await $fetch<SourceResponse>("/api/cms/source", {
        query: { path: route.path },
      });
      relPath.value = res.relPath;
      schemaFields.value = res.fields ?? [];
      baseSha.value = res.published.sha;
      if (res.draft) {
        hasDraft.value = true;
        frontmatterYaml.value = toYaml(res.draft.frontmatter);
        body.value = res.draft.body;
        baseSha.value = res.draft.baseSha ?? res.published.sha;
        lastSavedAt.value = res.draft.updatedAt;
      } else {
        hasDraft.value = false;
        frontmatterYaml.value = toYaml(res.published.frontmatter);
        body.value = res.published.body;
        lastSavedAt.value = null;
      }
      loadedBody.value = body.value;
      loadedFm.value = frontmatterYaml.value;
    } catch (e) {
      error.value = messageFrom(e);
    } finally {
      loading.value = false;
    }
  }

  /** Unsaved edits present since the last load/save. */
  function isDirty(): boolean {
    return body.value !== loadedBody.value || frontmatterYaml.value !== loadedFm.value;
  }

  function parseFrontmatter(): Record<string, unknown> {
    // Touch yamlReady so the `frontmatter` computed recomputes once yaml lands.
    if (!yamlReady.value || !yamlParse) return {};
    const parsed = yamlParse(frontmatterYaml.value || "") as unknown;
    if (parsed === null || parsed === undefined) return {};
    if (typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("Frontmatter must be a YAML mapping");
    }
    return parsed as Record<string, unknown>;
  }

  /**
   * Frontmatter as a structured object, backed by the canonical YAML string.
   * The Settings form reads/writes this; the setter reserializes to YAML so the
   * draft/diff/commit pipeline (which reads `frontmatterYaml`) is unaffected.
   */
  const frontmatter = computed<Record<string, unknown>>({
    get() {
      try {
        return parseFrontmatter();
      } catch {
        return {};
      }
    },
    set(obj) {
      frontmatterYaml.value = toYaml(obj);
    },
  });

  /**
   * Save the current buffer as a draft. `pathOverride` targets a route other
   * than the current one — used to autosave the previous page's edits when the
   * user navigates away (the watcher fires after `route.path` has changed).
   */
  async function saveDraft(pathOverride?: string): Promise<void> {
    saving.value = true;
    error.value = null;
    try {
      const frontmatter = parseFrontmatter();
      const res = await $fetch<{ ok: boolean; updatedAt: number }>(
        "/api/cms/draft",
        {
          method: "PUT",
          body: {
            path: pathOverride ?? route.path,
            frontmatter,
            body: body.value,
            baseSha: baseSha.value,
          },
        },
      );
      hasDraft.value = true;
      lastSavedAt.value = res.updatedAt;
      // Buffer is now persisted — mark clean so navigation doesn't re-save it.
      loadedBody.value = body.value;
      loadedFm.value = frontmatterYaml.value;
    } catch (e) {
      error.value = messageFrom(e);
      throw e;
    } finally {
      saving.value = false;
    }
  }

  /**
   * Persist the current buffer, then fetch the diff for EVERY pending draft —
   * the whole editing session is reviewed and published together.
   */
  async function fetchDiff(): Promise<{
    files: CmsDiffFile[];
    hasChanges: boolean;
  } | null> {
    error.value = null;
    try {
      await saveDraft();
      return await $fetch("/api/cms/diff", { method: "POST" });
    } catch (e) {
      error.value = messageFrom(e);
      return null;
    }
  }

  /** The editor's current open CMS PR (drives commit-vs-create-PR). */
  async function fetchPrStatus(): Promise<{
    open: boolean;
    url?: string;
    number?: number;
    branch: string;
    dev?: boolean;
  } | null> {
    try {
      return await $fetch("/api/cms/pr-status");
    } catch {
      return null;
    }
  }

  /** Commit all pending drafts to the editor's existing open PR ([skip ci]). */
  async function commitToPr(): Promise<{
    ok: boolean;
    url: string | null;
    dev?: boolean;
    count?: number;
  } | null> {
    publishing.value = true;
    error.value = null;
    try {
      const res = await $fetch<{
        ok: boolean;
        url: string | null;
        dev?: boolean;
        count?: number;
      }>("/api/cms/commit", { method: "POST" });
      hasDraft.value = false;
      lastSavedAt.value = null;
      return res;
    } catch (e) {
      error.value = messageFrom(e);
      throw e;
    } finally {
      publishing.value = false;
    }
  }

  /** Open one PR for all pending drafts (branch + commits + PR). PR-only. */
  async function createPr(
    title: string,
    description: string,
  ): Promise<{ ok: boolean; url: string | null; dev?: boolean; count?: number } | null> {
    publishing.value = true;
    error.value = null;
    try {
      const res = await $fetch<{
        ok: boolean;
        url: string | null;
        dev?: boolean;
        count?: number;
      }>("/api/cms/pr", { method: "POST", body: { title, description } });
      hasDraft.value = false;
      lastSavedAt.value = null;
      return res;
    } catch (e) {
      error.value = messageFrom(e);
      throw e;
    } finally {
      publishing.value = false;
    }
  }

  /** What DEPLOY would put live (content `production` → what accp serves). */
  async function fetchRelease(): Promise<CmsRelease | null> {
    try {
      return await $fetch<CmsRelease>("/api/cms/release");
    } catch (e) {
      error.value = messageFrom(e);
      return null;
    }
  }

  /** Promote the content this site serves to production (`sha` = what was reviewed). */
  async function deploy(
    sha: string,
  ): Promise<{ ok: boolean; count: number; runsUrl: string; dev?: boolean } | null> {
    deploying.value = true;
    error.value = null;
    try {
      return await $fetch("/api/cms/deploy", { method: "POST", body: { sha } });
    } catch (e) {
      error.value = messageFrom(e);
      return null;
    } finally {
      deploying.value = false;
    }
  }

  async function discard(): Promise<void> {
    error.value = null;
    try {
      await $fetch("/api/cms/draft", {
        method: "DELETE",
        query: { path: route.path },
      });
      hasDraft.value = false;
      await loadCurrent();
    } catch (e) {
      error.value = messageFrom(e);
    }
  }

  async function listDrafts(): Promise<CmsDraftDto[]> {
    try {
      return await $fetch<CmsDraftDto[]>("/api/cms/drafts");
    } catch {
      return [];
    }
  }

  return {
    canEdit,
    enabled,
    editing,
    open,
    loading,
    saving,
    publishing,
    error,
    relPath,
    frontmatterYaml,
    frontmatter,
    schemaFields,
    body,
    hasDraft,
    lastSavedAt,
    previewBody,
    previewing,
    probe,
    loadCurrent,
    saveDraft,
    isDirty,
    fetchDiff,
    fetchPrStatus,
    commitToPr,
    createPr,
    fetchRelease,
    deploy,
    deploying,
    editorName,
    editorEmail,
    discard,
    listDrafts,
    refreshPreview,
    clearPreview,
    previewValue,
  };
}

function toYaml(obj: Record<string, unknown>): string {
  if (!obj || Object.keys(obj).length === 0) return "";
  // Only reached after ensureYaml() (loadCurrent, or the settings-form setter that
  // renders post-load), so yamlStringify is always resolved here.
  return yamlStringify!(obj, { lineWidth: 0 });
}

function messageFrom(e: unknown): string {
  const d = (e as { data?: { statusMessage?: string }; statusMessage?: string });
  return (
    d?.data?.statusMessage ??
    d?.statusMessage ??
    (e as Error)?.message ??
    "Something went wrong"
  );
}
